import {Feature, FeatureCollection, Geometry} from "geojson";
import {SitumFeatureProperties, SitumLayerStyles} from "../types/Situm";
import {FillExtrusionPaint} from "mapbox-gl";

/**
 * Get a modified list of Situm features
 * @param features
 */
export function getModifiedSitumFeatures(features: Feature<Geometry, SitumFeatureProperties>[] = []) {
  return features.map(feature => {

    // If the feature is a stairs and has extrusionHeight, modify the category name so each will get its own layer
    if (feature.properties.category?.toLowerCase().includes('stairs') && 'extrusionHeight' in feature.properties) {
      return {
        ...feature,
        properties: {
          ...feature.properties,
          category: `${feature.properties.category}-${feature.properties.extrusionHeight}`,
        }
      }
    }

    // Return the original feature if no modifications are needed
    return feature;
  })
}

/**
 * Get a list of layers for the Situm building to be used in a Mapbox map.
 * @param buildingId
 * @param situmFeatureCollection
 * @param situmLayerStyles
 */
export function getSitumBuildingLayers(buildingId: number, situmFeatureCollection: FeatureCollection<Geometry, SitumFeatureProperties>, situmLayerStyles: SitumLayerStyles) {
  const categoryList = new Set();
  const layers: any[] = [];

  for (const feature of situmFeatureCollection.features) {
    if (!feature.properties?.category) continue;

    const category = feature.properties.category;
    const paint = getLayerPaintProperties(category, situmLayerStyles, feature);

    const layer = {
      type: 'fill-extrusion',
      id: `ngage-building-${category}-${buildingId}`,
      filter: ["==", "category", category],
      paint
    };

    // Add the layer only if the layer category doesn't already exist
    if (!categoryList.has(category)) {
      layers.push(layer);
      categoryList.add(category);
    }
  }

  return layers;
}


/**
 * Get the Mapbox paint properties for a given layer category.
 * @param category
 * @param layerStyles
 * @param feature
 */
function getLayerPaintProperties(category: string, layerStyles: SitumLayerStyles, feature: Feature<Geometry, SitumFeatureProperties>): FillExtrusionPaint {
  const styles = layerStyles[category] ?? layerStyles[`.${category}`] ?? layerStyles.default;
  const isStairsLayer = category.toLowerCase().includes("stairs");
  const fillExtrusionColor = isStairsLayer ? '#ffffff' : styles.fillColor ?? layerStyles.default.fillColor ?? '#000000';
  const fillExtrusionOpacity = isStairsLayer ? 1 : Number(styles.fillOpacity ?? 1);
  const fillExtrusionHeight = feature.properties.extrusionHeight ?? styles.extrusionHeight ?? 0;

  return {
    'fill-extrusion-color': fillExtrusionColor,
    'fill-extrusion-opacity': fillExtrusionOpacity,
    'fill-extrusion-height': fillExtrusionHeight,
  }
}